const ignore = () => undefined;
const dateFormat = new Intl.DateTimeFormat("en-US");
const printDateValue = (dateValue) => {
    return dateFormat.format(dateValue.value);
};
const printSight = {
    dealId: (t) => {
        return t.transaction.id + "/" + t.trancheIndex.toString();
    },
    country: (t) => {
        return t.meta?.parties?.find(p => p.type === 'ISSUER')?.country?.name;
    },
    currency: ({ data, override }) => {
        return override?.nominal?.amount?.currency ?? data?.nominal?.amount?.currency ?? data?.nominal?.tapAmount?.currency;
    },
    region2: (t) => {
        return t.transaction.regions?.[0];
    },
    region3: (t) => {
        return t.transaction.regions?.[1];
    },
    region4: (t) => {
        return t.transaction.regions?.[2];
    },
    product: (t) => {
        return t.transaction.sector;
    },
    parent: (t) => {
        return t.meta?.parties?.find(p => p.type === 'ISSUER')?.ultimateParent?.name;
    },
    issuer1: (t) => {
        // eslint-disable-next-line @typescript-eslint/prefer-find
        return t.meta?.parties?.filter(p => p.type === 'ISSUER')[0]?.company?.name;
    },
    ticker1: ({ data, override, meta }) => {
        return override?.issuer?.identifiers?.find(i => i.type === 'TICKER')?.value ??
            (meta?.parties?.find(p => p.type === 'ISSUER')?.company?.tickers?.length === 1 ? meta.parties.find(p => p.type === 'ISSUER')?.company?.tickers?.[0] : undefined) ??
            data?.issuer?.identifiers?.find(i => i.type === 'TICKER')?.value;
    },
    issuer2: (t) => {
        return t.meta?.parties?.filter(p => p.type === 'ISSUER')[1]?.company?.name;
    },
    ticker2: ignore,
    series: ignore,
    sector: (map) => ({ meta }) => {
        const issuer = meta?.parties?.find(p => p.type === 'ISSUER');
        const value = issuer?.company?.industry;
        if (value !== undefined) {
            return map.get(value);
        }
        return undefined;
    },
    subSector: (map) => ({ meta }) => {
        const issuer = meta?.parties?.find(p => p.type === 'ISSUER');
        const value = issuer?.company?.sector;
        if (value !== undefined) {
            return map.get(value);
        }
        return undefined;
    },
    announced: (t) => {
        return dateFormat.format(t.transaction.firstUpdate);
    },
    pricing: ({ data, meta, override }) => {
        if (override?.pricingDate !== undefined) {
            return dateFormat.format(new Date(override.pricingDate));
        }
        if (meta?.pricingDate !== undefined) {
            return dateFormat.format(meta.pricingDate);
        }
        if (data?.pricingDate !== undefined) {
            return dateFormat.format(new Date(data.pricingDate));
        }
        return undefined;
    },
    settlement: ({ data, override }) => {
        const date = override?.settlement?.date ?? data?.settlement?.date;
        return date !== undefined ? dateFormat.format(date) : undefined;
    },
    class: ({ data, override }) => {
        return override?.maturity?.tenor?.print() ?? data?.maturity?.tenor?.print();
    },
    size: ({ data, override }) => {
        const size = override?.nominal?.amount?.size ?? data?.nominal?.tapAmount?.size ?? data?.nominal?.amount?.size;
        if (size === undefined) {
            return undefined;
        }
        const value = size.print();
        return value === undefined || typeof value === 'string' ? value : value.value.toString();
    },
    wal: ({ data, override }) => {
        const wal = override?.maturity?.weightedAverageLife ?? data?.maturity?.weightedAverageLife;
        return wal?.print();
    },
    walExt: ignore,
    ranking: (map) => ({ data, override }) => {
        const values = override?.documentation?.statusOfNotes ?? data?.documentation?.statusOfNotes;
        return values?.map(v => map.get(v)).join(", ");
    },
    tenor: ({ data, override }) => {
        const tenor = override?.maturity?.tenor ?? data?.maturity?.tenor;
        return tenor?.print();
    },
    maturityExp: ignore,
    maturityLegal: ({ data, override }) => {
        if (override?.maturity?.date !== undefined) {
            return printDateValue(override.maturity.date.print());
        }
        if (override?.maturity?.perpetual === true || override?.maturity?.tenor?.perpetual) {
            return 'Perpetual';
        }
        if (data?.maturity !== undefined) {
            const { maturity } = data;
            if (maturity.scheduledDissolutionDate !== undefined) {
                const dateRegex = /(?<year>[0-9]{4})(-(?<month>[0-9]{2})(-(?<day>[0-9]{2}))?)?/;
                const dateMatchArray = dateRegex.exec(maturity.scheduledDissolutionDate);
                const groups = dateMatchArray?.groups;
                if (groups !== undefined) {
                    const { year } = groups;
                    const month = 'month' in groups ? groups.month : undefined;
                    const day = 'day' in groups ? groups.day : undefined;
                    const yearValue = parseInt(year, 10);
                    const monthValue = month !== undefined ? parseInt(month, 10) : 0;
                    const dayValue = day !== undefined ? parseInt(day, 10) : 1;
                    const realDate = new Date(Date.UTC(yearValue, monthValue - 1, dayValue));
                    let precision = 'Y';
                    if (month !== undefined)
                        precision = 'YM';
                    if (day !== undefined)
                        precision = 'YMD';
                    return printDateValue({ value: realDate, options: { precision } });
                }
            }
            if (maturity.date !== undefined) {
                return printDateValue(maturity.date.print());
            }
            if (maturity.perpetual || maturity.tenor?.perpetual) {
                return 'Perpetual';
            }
        }
        return undefined;
    },
    ce: ignore,
    ltv: ignore,
    type: ignore,
    benchmark: ({ data, override }) => {
        const benchmark = override?.pricing?.spread?.benchmark ?? data?.pricing?.spread?.benchmark;
        return benchmark?.print();
    },
    ipt: ({ data, override }) => {
        const ipt = override?.initialPriceThoughts ?? data?.initialPriceThoughts;
        return ipt?.print();
    },
    gdnc: ({ data, override }) => {
        const guidance = override?.guidance ?? data?.guidance;
        return guidance?.print();
    },
    sprd: ({ data, override }) => {
        const spread = override?.pricing?.spread?.value ?? data?.pricing?.spread?.value;
        return spread?.toString();
    },
    cpn: ({ data, override }) => {
        if (override?.coupons?.[0].fixedRate !== undefined || override?.coupons?.[0].floatingRate !== undefined) {
            const [coupon] = override.coupons;
            if (coupon.fixedRate?.value !== undefined) {
                return coupon.fixedRate.value.toString();
            }
            if (coupon.floatingRate?.referenceRate !== undefined && coupon.floatingRate.margin !== undefined) {
                const items = [];
                items.push(coupon.floatingRate.referenceRate.printShort());
                const margin = coupon.floatingRate.margin === 0 ? 'flat' : `${coupon.floatingRate.margin > 0 ? '+' : ''}${coupon.floatingRate.margin.toString()}`;
                items.push(margin);
                return items.join(' ');
            }
            return undefined;
        }
        if (data !== undefined) {
            const coupons = data.coupons ?? data.profitRates;
            if (coupons !== undefined) {
                const [coupon] = coupons;
                if (coupon.fixedRate?.value !== undefined) {
                    return coupon.fixedRate.value.toString();
                }
                if (coupon.floatingRate?.referenceRate !== undefined && coupon.floatingRate.margin !== undefined) {
                    const items = [];
                    items.push(coupon.floatingRate.referenceRate.printShort());
                    const margin = coupon.floatingRate.margin === 0 ? 'flat' : `${coupon.floatingRate.margin > 0 ? '+' : ''}${coupon.floatingRate.margin.toString()}`;
                    items.push(margin);
                    return items.join(' ');
                }
            }
        }
    },
    yld: ({ data, override }) => {
        const value = override?.pricing?.yield?.value ?? data?.pricing?.yield?.value;
        return value !== undefined ? value.toString() : undefined;
    },
    price: ({ data, override }) => {
        const pricing = override?.pricing ?? data?.pricing;
        return pricing?.printPrice()?.value.toString();
    },
    nic: ignore,
    bookSize: ({ data, override }) => {
        const value = override?.management?.books?.size?.value ?? data?.management?.books?.size?.value;
        return value !== undefined ? (value / 1000000).toString() : undefined;
    },
    cusip: ({ data, override }) => {
        if (data?.identifiers !== undefined) {
            const isins = data.identifiers.filter((i) => i.type === 'CUSIP' && 'value' in i);
            if (isins.length === 1) {
                return isins[0].value;
            }
            if (isins.length > 1) {
                const isin144A = isins.find(i => i.regulation === 'RULE_144A');
                if (isin144A !== undefined) {
                    return isin144A.value;
                }
                return isins[0].value;
            }
        }
        return undefined;
    },
    isins: ({ data, override }) => {
        if (override?.identifiers !== undefined) {
            return override.identifiers.filter((i) => i.type === 'ISIN' && i.value !== undefined && i.value !== '')
                .map((i) => (i.value ?? "ERROR") + (i.regulation !== undefined ? ` (${i.regulation === 'REGS' ? 'RegS' : '144A'})` : '')).join(', ');
        }
        if (data?.identifiers !== undefined) {
            return data.identifiers.filter((i) => i.type === 'ISIN' && i.value !== undefined && i.value !== '')
                .map((i) => (i.value ?? "ERROR") + (i.regulation !== undefined ? ` (${i.regulation === 'REGS' ? 'RegS' : '144A'})` : '')).join(', ');
        }
        return undefined;
    },
    registration: (map) => ({ data, override }) => {
        const value = override?.documentation?.usSales ?? data?.documentation?.usSales;
        if (value === undefined) {
            return undefined;
        }
        return value.map((v) => map.get(v)).join(", ");
    },
    erisa: ignore,
    retained: ignore,
    io: ignore,
    upsized: ignore,
    verified: ignore,
    rating: (agency) => ({ data, override }) => {
        const printRatings = (ratings) => {
            if (ratings.unrated) {
                return '-';
            }
            const { values } = ratings;
            if (values !== undefined) {
                const issueRating = values.find((r) => r.agency === agency)?.value;
                if (issueRating !== undefined) {
                    return issueRating;
                }
                if (!values.some(r => r.agency !== undefined)) {
                    if (values.length > 0 && agency === 'MOODYS') {
                        return values[0].value;
                    }
                    if (values.length > 1 && agency === 'S_P') {
                        return values[1].value;
                    }
                    if (values.length > 2 && agency === 'FITCH') {
                        return values[2].value;
                    }
                }
            }
            return undefined;
        };
        if (override !== undefined) {
            const { ratings } = override;
            if (ratings !== undefined && ((ratings.values !== undefined && ratings.values.length > 0) || ratings.unrated)) {
                return printRatings(ratings);
            }
        }
        if (data === undefined) {
            return undefined;
        }
        const { ratings } = data;
        if (ratings !== undefined && ((ratings.values !== undefined && ratings.values.length > 0) || ratings.unrated)) {
            return printRatings(ratings);
        }
        const issuerRatings = data.issuer?.ratings;
        if (issuerRatings !== undefined && ((issuerRatings.values !== undefined && issuerRatings.values.length > 0) || issuerRatings.unrated)) {
            return printRatings(issuerRatings);
        }
        return undefined;
    },
    sl: (index) => ignore,
    jb: (index) => ({ meta }) => {
        if (meta?.bookrunners !== undefined) {
            const finalOnes = meta.bookrunners.length > 8 ? meta.bookrunners.filter((b) => b.roles?.includes('GLOBAL_COORDINATOR')) : meta.bookrunners;
            if (finalOnes.length > index - 1) {
                return finalOnes[index - 1].name;
            }
        }
        return undefined;
    },
    collateral: ignore,
    guarantors: ignore,
    originators: ignore,
    riskRetention: ignore,
    useOfProceeds: ({ override, data }) => {
        return override?.useOfProceeds ?? data?.useOfProceeds;
    },
    volckerCompliance: ignore,
    firstPayDate: ({ override, data }) => {
        const firstCouponDate = override?.coupons?.[0].firstCouponDate ?? data?.coupons?.[0].firstCouponDate;
        if (firstCouponDate !== undefined) {
            return dateFormat.format(new Date(firstCouponDate));
        }
        return undefined;
    },
    minDenomination: ({ override, data }) => {
        const denominations = override?.documentation?.denominations ?? data?.documentation?.denominations;
        return denominations?.printNoCurrency();
    },
    redemption: ignore,
    cutOffBalance: ignore,
    expPricingDate: ignore,
    expRatingDate: ignore,
    expSettlementDate: ignore,
    pricingSpeed: ignore,
    noOfLoans: ignore,
    noOfProperties: ignore,
    top10Loans: ignore,
    waCutOffLTV: ignore,
    waMatLTV: ignore,
    waRemAmrtTerm: ignore,
    waSeasoning: ignore,
    waTermToMat: ignore,
    waUWDSCR: ignore,
    waUWNoiDY: ignore,
    reinvPeriod: ignore,
    reinvPeriodED: ignore,
    refinancing: ignore,
    reset: ignore,
    walTestPeriod: ignore,
    walTestPeriodED: ignore,
    windowStart: ignore,
    windowEnd: ignore,
    comments: ignore,
    absMaturity: ignore
};
export default printSight;
